// Rounds.tsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { requireAccessToken } from '../utils/authToken';
import Modal from '../components/addNewRoundModal';
import ViewRoundByHole from '../components/ViewRoundByHole';
import SeasonFilter from '../components/seasonFilter';
import PenaltyBadge from '../components/PenaltyBadge';
import FitTable from '../components/FitTable';
import PlayerName from '../components/PlayerName';
import { compareMatchesInDay } from '../utils/matchOrder';
import {
  buildLegacyStandings, buildScheduledStandings, standingsFromLegacy, standingsFromScheduled,
} from '../utils/majorLeaderboard';
import MajorAnalysis from '../components/majorAnalysis/MajorAnalysis';
import ScorecardUploadButton from '../components/ScorecardUploadButton';
import { Link } from 'react-router-dom';
import { OPEN_STATUSES, RoundScorecardSummary, scorecardApi, statusLabel } from '../utils/scorecardApi';
import '../css/rounds.css';
import '../css/scorecard.css';

interface RoundPlayer {
  player_id?: number;
  first_name: string;
  last_name: string;
  gross_score: number;
  course_handicap: number;
  net_score: number;
  rank: number;
  points?: number;
  is_disqualified?: boolean;
  dq_reason?: string | null;
  is_guest?: boolean;
  /** Penalty strokes already included in net_score (applied after handicap). */
  penalty_strokes?: number;
  penalty_reason?: string | null;
}

interface Round {
  round_id: number;
  date: string;
  course_name: string;
  facility_name?: string | null;
  course_teebox_name: string;
  players: RoundPlayer[];
}

interface ScheduleEvent {
  round_number: number;
  round_id: number | null;
  slot_date: string | null;
  am_pm: 'AM' | 'PM' | null;
  is_leaderboard_round: boolean;
  is_final_round: boolean;
  personal_round: number | null;
  gross_score: number | null;
  net_score: number | null;
}

interface PlayerSchedule {
  player_id: number;
  first_name: string;
  last_name: string;
  events: ScheduleEvent[];
}

interface LBGroupPlayer {
  player_id: number;
  first_name: string;
  last_name: string;
}

interface LBGroup {
  group_id: number;
  group_number: number;
  slot_date: string | null;
  am_pm: 'AM' | 'PM' | null;
  course_name: string | null;
  tee_time: string | null;
  is_final_round: boolean;
  players: LBGroupPlayer[];
}

interface RoundMeta {
  round_id: number;
  round_number: number | null;
  is_leaderboard_round: boolean;
  is_final_round: boolean;
  slot_date: string | null;
  am_pm: 'AM' | 'PM' | null;
  tee_time: string | null;
  /** Every tee time the match covers (a match can span several on one course). */
  tee_times?: string[];
  reservation_name: string | null;
  reservation_course_name: string | null;
  complex_name: string | null;
  /** A session split across courses is several matches: this is match i of n. */
  session_match_index?: number | null;
  session_match_count?: number | null;
}

interface RoundsContext {
  per_player_schedule: PlayerSchedule[];
  leaderboard_groups: LBGroup[];
  round_meta?: Record<string, RoundMeta>;
}

interface TournamentResult {
  player_id: number;
  first_name: string;
  last_name: string;
  total_score: number | null;
  rank: number | null;
  num_rounds: number | null;
  num_players: number | null;
  is_dq: boolean;
  is_guest: boolean;
  placement_points: number | string; // pg numeric can arrive as string
  team_points: number | string;
  total_points: number | string;
}

const s: Record<string, React.CSSProperties> = {
  page: {
    background: '#f8fafc',
    minHeight: '100vh',
    padding: '24px 16px',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  // Centered content column that fills small screens and caps on large ones.
  inner: {
    maxWidth: 1120,
    margin: '0 auto',
    width: '100%',
  },
  topBar: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 20,
  },
  filterLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: '#64748b',
  },
  select: {
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    padding: '7px 12px',
    fontSize: 14,
    color: '#1e293b',
    background: '#fff',
    cursor: 'pointer',
    outline: 'none',
  },
  addBtn: {
    background: '#2563eb',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '8px 18px',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    marginLeft: 'auto',
  },
  sectionCard: {
    background: '#fff',
    borderRadius: 12,
    boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
    border: '1px solid #e2e8f0',
    marginBottom: 24,
    overflow: 'hidden',
  },
  sectionHeader: {
    padding: '14px 20px',
    borderBottom: '1px solid #f1f5f9',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: '#1e293b',
    margin: 0,
  },
  badge: {
    background: '#f1f5f9',
    color: '#64748b',
    borderRadius: 20,
    padding: '2px 10px',
    fontSize: 12,
    fontWeight: 600,
  },
  roundsList: {
    padding: '20px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 22,
  },
  roundCard: {
    background: '#fff',
    borderRadius: 12,
    border: '1px solid #d8dfe8',
    boxShadow: '0 2px 8px rgba(15,23,42,0.08)',
    overflow: 'hidden',
  },
  roundCardHeader: {
    padding: '14px 18px',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 16,
    flexWrap: 'wrap' as const,
    background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
    borderBottom: '1px solid #e2e8f0',
    borderLeft: '4px solid #2563eb',
  },
  roundInfo: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 5,
  },
  roundDate: {
    display: 'inline-block',
    fontSize: 11,
    color: '#475569',
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase' as const,
    background: '#e2e8f0',
    borderRadius: 5,
    padding: '2px 8px',
    alignSelf: 'flex-start',
  },
  roundCourseLine: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 1,
  },
  roundFacility: {
    fontSize: 13,
    color: '#64748b',
    fontWeight: 500,
  },
  roundCourseName: {
    fontSize: 18,
    fontWeight: 800,
    color: '#0f172a',
    letterSpacing: '-0.01em',
  },
  roundTeebox: {
    fontSize: 12,
    color: '#1d4ed8',
    background: '#dbeafe',
    borderRadius: 5,
    padding: '2px 9px',
    fontWeight: 600,
  },
  roundActions: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    flexShrink: 0,
  },
  btnInfo: {
    background: '#eff6ff',
    color: '#2563eb',
    border: '1px solid #bfdbfe',
    borderRadius: 7,
    padding: '5px 12px',
    fontSize: 12,
    fontWeight: 500,
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
  },
  btnDanger: {
    background: '#fff',
    color: '#ef4444',
    border: '1px solid #fecaca',
    borderRadius: 7,
    padding: '5px 12px',
    fontSize: 12,
    fontWeight: 500,
    cursor: 'pointer',
  },
  emptyState: {
    padding: '32px 20px',
    textAlign: 'center' as const,
    color: '#94a3b8',
    fontSize: 14,
  },
  breakdownCard: {
    background: '#fff',
    borderRadius: 12,
    boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
    border: '1px solid #e2e8f0',
    marginBottom: 24,
    overflow: 'hidden',
  },
  bdHeader: {
    padding: '14px 20px',
    borderBottom: '1px solid #f1f5f9',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap' as const,
  },
  bdTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: '#1e293b',
    margin: 0,
  },
};

const teeboxBadge = (name: string): React.CSSProperties => {
  const base: React.CSSProperties = {
    fontSize: 12, borderRadius: 5, padding: '2px 9px', fontWeight: 600, display: 'inline-block',
  };
  const n = (name || '').toLowerCase();
  const palette: { keys: string[]; bg: string; color: string; border?: string }[] = [
    { keys: ['onyx'], bg: '#0b0f19', color: '#e5e7eb' },
    { keys: ['charcoal'], bg: '#374151', color: '#f9fafb' },
    { keys: ['black'], bg: '#1f2937', color: '#f9fafb' },
    { keys: ['navy'], bg: '#1e3a8a', color: '#ffffff' },
    { keys: ['blue'], bg: '#2563eb', color: '#ffffff' },
    { keys: ['white'], bg: '#ffffff', color: '#1e293b', border: '1px solid #cbd5e1' },
    { keys: ['red'], bg: '#dc2626', color: '#ffffff' },
    { keys: ['gold'], bg: '#d4af37', color: '#3a2f00' },
    { keys: ['silver'], bg: '#c0c0c0', color: '#1e293b' },
    { keys: ['green'], bg: '#16a34a', color: '#ffffff' },
    { keys: ['yellow'], bg: '#facc15', color: '#422006' },
    { keys: ['bronze', 'copper'], bg: '#cd7f32', color: '#ffffff' },
    { keys: ['purple', 'violet'], bg: '#7c3aed', color: '#ffffff' },
    { keys: ['orange'], bg: '#ea580c', color: '#ffffff' },
    { keys: ['pink', 'rose'], bg: '#ec4899', color: '#ffffff' },
    { keys: ['teal', 'aqua'], bg: '#14b8a6', color: '#ffffff' },
    { keys: ['tan', 'sand', 'beige'], bg: '#d2b48c', color: '#3a2f00' },
    { keys: ['gray', 'grey'], bg: '#9ca3af', color: '#111827' },
  ];
  for (const p of palette) {
    if (p.keys.some(k => n.includes(k))) {
      return { ...base, background: p.bg, color: p.color, ...(p.border ? { border: p.border } : {}) };
    }
  }
  return { ...base, background: '#e2e8f0', color: '#475569' };
};

const rankBadge = (rank: number): React.CSSProperties => {
  const map: Record<number, { bg: string; color: string }> = {
    1: { bg: '#fef3c7', color: '#92400e' },
    2: { bg: '#f1f5f9', color: '#475569' },
    3: { bg: '#fef0e7', color: '#9a3412' },
  };
  const c = map[rank] || { bg: '#f8fafc', color: '#94a3b8' };
  return {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: '2em', height: '2em', borderRadius: '50%',
    background: c.bg, color: c.color, fontWeight: 700, fontSize: '0.92em', flexShrink: 0,
  };
};

function fmtLongDate(iso: string | null | undefined) {
  if (!iso) return '';
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-CA', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });
}

const Rounds: React.FC = () => {
  const [tournaments, setTournaments] = useState<any[]>([]);
  const [selectedTournamentId, setSelectedTournamentId] = useState<number | null>(null);
  const { getAccessTokenSilently, user } = useAuth0();
  const [regularRounds, setRegularRounds] = useState<Round[]>([]);
  // Per round: scorecard photos (open reviews) and hole-by-hole coverage.
  const [cardSummaries, setCardSummaries] = useState<Record<string, RoundScorecardSummary>>({});
  const [tournamentRounds, setTournamentRounds] = useState<Round[]>([]);
  const [tournamentScores, setTournamentScores] = useState<any[]>([]);
  const [tournamentResults, setTournamentResults] = useState<TournamentResult[]>([]);
  const [scheduleContext, setScheduleContext] = useState<RoundsContext | null>(null);
  const [modalShow, setModalShow] = useState(false);
  const [viewByHoleModalOpen, setViewByHoleModalOpen] = useState(false);
  const [selectedRoundId, setSelectedRoundId] = useState<number | null>(null);
  // Leaderboard view: Net / Gross scores, or the Points breakdown. `showGross`
  // (used to pick net vs gross scores) is derived; 'points' uses net-ordered rows.
  const [viewMode, setViewMode] = useState<'net' | 'gross' | 'points'>('net');
  const showGross = viewMode === 'gross';
  const hasPoints = tournamentResults.length > 0; // major has computed points
  const showPoints = viewMode === 'points' && hasPoints;
  const fmtPts = (v: number | string | null | undefined) =>
    v == null ? '—' : `${Math.round(Number(v) * 100) / 100}`;
  const [selectedSeason, setSelectedSeason] = useState<string>('');
  // A selected major shows its Leaderboard (and matches) or its Analysis.
  const [majorView, setMajorView] = useState<'leaderboard' | 'analysis'>('leaderboard');
  // Bumped when round scores may have changed, so the Analysis reloads.
  const [analysisReload, setAnalysisReload] = useState(0);
  // Which major the rounds / schedule context currently in state belong to —
  // the Analysis waits for both so its order never comes from a stale major.
  const [roundsLoadedFor, setRoundsLoadedFor] = useState<number | null | undefined>(undefined);
  const [contextLoadedFor, setContextLoadedFor] = useState<number | null | undefined>(undefined);

  // Switching season leaves the major that was open: the page flips to the new
  // season's regular rounds and the Tournament dropdown reloads for that season.
  // Stable on purpose — SeasonFilter's effect depends on this callback.
  const handleSeasonChange = useCallback((season: string) => {
    setSelectedSeason(season);
    setSelectedTournamentId(null);
    setTournaments([]);
  }, []);

  const APIURL = process.env.REACT_APP_API_URL;

  const handleModalClose = () => setModalShow(false);
  const handleModalShow = () => setModalShow(true);
  const handleViewByHoleModalClose = () => setViewByHoleModalOpen(false);
  const handleViewByHoleModalShow = (roundId: number) => {
    setSelectedRoundId(roundId);
    setViewByHoleModalOpen(true);
  };

  useEffect(() => {
    if (!selectedSeason) return;
    let stale = false; // a previous season's slow response must not refill the dropdown
    const fetchTournaments = async () => {
      try {
        const token = await getAccessTokenSilently();
        const res = await fetch(`${APIURL}/tournaments?season_year=${selectedSeason}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Failed to fetch tournaments');
        const list = await res.json();
        if (!stale) setTournaments(list);
      } catch (err) {
        console.error('Error fetching tournaments:', err);
      }
    };
    fetchTournaments();
    return () => { stale = true; };
  }, [APIURL, getAccessTokenSilently, selectedSeason]);

  const fetchRegularRounds = useCallback(async () => {
    if (!selectedSeason) return;
    try {
      const token = await getAccessTokenSilently();
      const res = await fetch(`${APIURL}/rounds?season_year=${selectedSeason}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Network response was not ok');
      setRegularRounds(await res.json());
    } catch (error) {
      console.error('Error fetching regular rounds:', error);
    }
  }, [APIURL, getAccessTokenSilently, selectedSeason]);

  const fetchTournamentRounds = useCallback(async () => {
    if (!selectedSeason) return;
    try {
      const token = await getAccessTokenSilently();
      let url = `${APIURL}/tournamentRounds?season_year=${selectedSeason}`;
      if (selectedTournamentId) url += `&tournament_id=${selectedTournamentId}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Network response was not ok');
      setTournamentRounds(await res.json());
      setRoundsLoadedFor(selectedTournamentId);
    } catch (error) {
      console.error('Error fetching tournament rounds:', error);
    }
  }, [APIURL, getAccessTokenSilently, selectedSeason, selectedTournamentId]);

  const fetchTournamentScores = useCallback(async () => {
    if (!selectedTournamentId) return;
    try {
      const token = await getAccessTokenSilently();
      const res = await fetch(`${APIURL}/tournamentScores/${selectedTournamentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch tournament scores');
      setTournamentScores(await res.json());
    } catch (error) {
      console.error('Error fetching tournament scores:', error);
    }
  }, [APIURL, getAccessTokenSilently, selectedTournamentId]);

  // Per-major points (placement + team bonus + total) for the leaderboard.
  const fetchTournamentResults = useCallback(async () => {
    if (!selectedTournamentId) { setTournamentResults([]); return; }
    try {
      const token = await getAccessTokenSilently();
      const res = await fetch(`${APIURL}/tournaments/${selectedTournamentId}/results`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { setTournamentResults([]); return; }
      const data = await res.json();
      setTournamentResults(data.results || []);
    } catch { setTournamentResults([]); }
  }, [APIURL, getAccessTokenSilently, selectedTournamentId]);

  const fetchScheduleContext = useCallback(async () => {
    if (!selectedTournamentId) { setScheduleContext(null); setContextLoadedFor(null); return; }
    try {
      const token = await getAccessTokenSilently();
      const res = await fetch(`${APIURL}/tournaments/${selectedTournamentId}/rounds-context`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { setScheduleContext(null); return; }
      setScheduleContext(await res.json());
    } catch { setScheduleContext(null); }
    finally { setContextLoadedFor(selectedTournamentId); }
  }, [APIURL, getAccessTokenSilently, selectedTournamentId]);

  useEffect(() => {
    fetchRegularRounds();
    fetchTournamentRounds();
    fetchTournamentScores();
    fetchTournamentResults();
    fetchScheduleContext();
  }, [fetchRegularRounds, fetchTournamentRounds, fetchTournamentScores, fetchTournamentResults, fetchScheduleContext]);

  const fetchCardSummaries = useCallback(async () => {
    const ids = [...regularRounds, ...tournamentRounds].map(r => r.round_id);
    if (!ids.length) return;
    try {
      setCardSummaries(await scorecardApi.summaries(requireAccessToken(getAccessTokenSilently), ids));
    } catch (err) {
      console.error('Error fetching scorecard summaries:', err);
    }
  }, [regularRounds, tournamentRounds, getAccessTokenSilently]);

  useEffect(() => { fetchCardSummaries(); }, [fetchCardSummaries]);

  const renderScorecardChip = (roundId: number) => {
    const sum = cardSummaries[roundId];
    if (!sum) return null;
    const open = sum.uploads.filter(u => OPEN_STATUSES.includes(u.status));
    if (open.length) {
      const u = open[0];
      const tone = u.status === 'ready' ? 'ready' : u.status === 'reading' || u.status === 'agent_working' ? 'reading' : 'review';
      const text = `📷 Scorecard: ${statusLabel(u.status)}${open.length > 1 ? ` (+${open.length - 1})` : ''}`;
      return u.reviewer_user_id === user?.sub
        ? <Link to={`/scorecard-review/${u.upload_id}`} className={`sc-chip-status sc-chip-status--${tone}`}>{text} →</Link>
        : <span className={`sc-chip-status sc-chip-status--${tone}`}>{text}</span>;
    }
    if (sum.with_holes > 0) {
      return (
        <button
          type="button"
          className="sc-chip-status sc-chip-status--holes"
          style={{ border: 0, cursor: 'pointer' }}
          onClick={() => handleViewByHoleModalShow(roundId)}
        >
          Hole-by-hole {sum.with_holes}/{sum.players}
        </button>
      );
    }
    return null;
  };

  const deleteRound = async (roundId: number) => {
    if (!window.confirm('Are you sure you want to delete this round?')) return;
    try {
      const token = await getAccessTokenSilently();
      const res = await fetch(`${APIURL}/rounds/${roundId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to delete the round');
      setRegularRounds(prev => prev.filter(r => r.round_id !== roundId));
      setTournamentRounds(prev => prev.filter(r => r.round_id !== roundId));
      fetchScheduleContext();
    } catch (error) {
      console.error('Error deleting round:', error);
    }
  };

  // ── Derive tournament gating from per-player schedule ────────────────────

  // score lookup: player_id → round_id → { gross, net }
  // The backend (/rounds-context) is the single source of truth for which score
  // belongs to which personal round: it assigns each player's completed rounds
  // chronologically (1st played → personal round 1, etc.). We do NOT re-match
  // scores on the client — doing so re-scatters them onto scheduler-round slots.
  const getEventScore = (event: ScheduleEvent): { gross: number; net: number } | null => {
    if (event.gross_score == null) return null;
    return { gross: event.gross_score, net: event.net_score ?? event.gross_score };
  };

  const pps = scheduleContext?.per_player_schedule ?? [];
  const lbGroups = scheduleContext?.leaderboard_groups ?? [];
  const roundMeta = scheduleContext?.round_meta ?? {};

  // For each player: ALL scheduled rounds (including the final round) in
  // personal_round order — every scheduled round counts toward the standings.
  // We also keep the pre-final subset on its own to drive the final-groupings
  // announcement, which is based on the standings *going into* the final round.
  type PersonalEvent = ScheduleEvent & { score: { gross: number; net: number } | null };
  const playerCounting = new Map<number, PersonalEvent[]>();   // all rounds, incl. final
  const playerPreFinal = new Map<number, PersonalEvent[]>();   // regular rounds only
  for (const pp of pps) {
    const all = pp.events
      .filter(e => e.personal_round != null)
      .sort((a, b) => (a.personal_round! - b.personal_round!))
      .map(e => ({ ...e, score: getEventScore(e) }));
    playerCounting.set(pp.player_id, all);
    playerPreFinal.set(pp.player_id, all.filter(e => !e.is_final_round && !e.is_leaderboard_round));
  }

  // Every player is scheduled for the same number of rounds (the tournament's
  // configured round count), so this is how many columns the leaderboard expects
  // from everyone — e.g. 3 for a 3-round major (2 regular rounds + the final).
  const expectedRounds = pps.length > 0
    ? Math.max(...pps.map(p => playerCounting.get(p.player_id)?.length ?? 0))
    : 0;
  const maxPreFinalRounds = pps.length > 0
    ? Math.max(...pps.map(p => playerPreFinal.get(p.player_id)?.length ?? 0))
    : 0;

  // gatedCount = how many rounds (in schedule order) EVERY player has completed
  // consecutively. The final round releases only once all players finish it.
  let gatedCount = 0;
  for (let n = 1; n <= expectedRounds; n++) {
    const allDone = pps.every(pp => (playerCounting.get(pp.player_id) ?? [])[n - 1]?.score != null);
    if (allDone) gatedCount = n;
    else break;
  }

  // How many pre-final rounds all players have finished — drives the final
  // groupings announcement independently of the final round itself.
  let preFinalGated = 0;
  for (let n = 1; n <= maxPreFinalRounds; n++) {
    const allDone = pps.every(pp => (playerPreFinal.get(pp.player_id) ?? [])[n - 1]?.score != null);
    if (allDone) preFinalGated = n;
    else break;
  }

  // Each player's released rounds (personal-round order) — the leaderboard's
  // input and, via the net standings, the Analysis view's default order.
  const scheduledEntries = pps.map(pp => ({
    player_id: pp.player_id,
    name: `${pp.first_name} ${pp.last_name}`,
    scores: (playerCounting.get(pp.player_id) ?? []).slice(0, gatedCount).map(e => e.score),
  }));

  // round_ids that are within the released window (for filtering round cards)
  const visibleRoundIds = new Set<number>();
  for (const pp of pps) {
    (playerCounting.get(pp.player_id) ?? []).slice(0, gatedCount).forEach(e => {
      if (e.round_id) visibleRoundIds.add(e.round_id);
    });
  }

  // (player_id, round_id) pairs whose score is released = within the player's
  // gated personal-round window. A round card can be visible because it is one
  // player's released round while still containing another player's not-yet-
  // released round (e.g. their personal round 2) — those scores stay blurred.
  const releasedPlayerRounds = new Set<string>();        // `${player_id}:${round_id}`
  const nameToPlayerId = new Map<string, number>();       // "First Last" → player_id
  for (const pp of pps) {
    nameToPlayerId.set(`${pp.first_name} ${pp.last_name}`, pp.player_id);
    (playerCounting.get(pp.player_id) ?? []).forEach((e, idx) => {
      if (e.round_id && idx < gatedCount) {
        releasedPlayerRounds.add(`${pp.player_id}:${e.round_id}`);
      }
    });
  }

  // Is this player's score in this round currently unlocked?
  // Defaults to released when we can't map the player (e.g. legacy/no schedule).
  const isScoreReleased = (roundId: number, firstName: string, lastName: string): boolean => {
    if (pps.length === 0) return true;
    const pid = nameToPlayerId.get(`${firstName} ${lastName}`);
    if (pid == null) return true;
    return releasedPlayerRounds.has(`${pid}:${roundId}`);
  };

  // Show final groupings once every player has finished all pre-final rounds
  // (i.e. the standings going into the final round are settled).
  const allPreLbDone = maxPreFinalRounds > 0 && preFinalGated >= maxPreFinalRounds;

  // ── Render final groupings announcement ────────────────────────────────────

  const renderFinalGroupingsAnnouncement = (minimized = false) => {
    if (lbGroups.length === 0) return null;
    const date = lbGroups[0]?.slot_date;
    const course = lbGroups[0]?.course_name;
    const isFinal = lbGroups.some(g => g.is_final_round);

    // Minimized: once the tournament is decided and a champion is declared, the
    // pairings collapse to a compact record — the champion + standings headline.
    if (minimized) {
      return (
        <div style={{
          background: '#fff', border: '1px solid #e2e8f0', borderLeft: '3px solid #cbd5e1',
          borderRadius: 8, padding: '9px 14px', marginBottom: 14,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{
              fontSize: 10, fontWeight: 700, textTransform: 'uppercase' as const,
              letterSpacing: '0.05em', color: '#64748b',
            }}>
              {isFinal ? 'Final-round pairings' : 'Leaderboard pairings'}
            </span>
            {date && <span style={{ fontSize: 11, color: '#94a3b8' }}>· {fmtLongDate(date)}</span>}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 20px', marginTop: 6 }}>
            {lbGroups.map(g => (
              <div key={g.group_id} style={{ fontSize: 12, color: '#475569' }}>
                <span style={{ fontWeight: 700, color: '#94a3b8', marginRight: 6 }}>
                  G{g.group_number}{g.tee_time ? ` · ${g.tee_time}` : ''}
                </span>
                {g.players.map(p => `${p.first_name} ${p.last_name[0] ?? ''}.`).join(', ')}
              </div>
            ))}
          </div>
        </div>
      );
    }

    // Full announcement — the headline while heading INTO the final round.
    const initials = (p: LBGroupPlayer) =>
      `${p.first_name[0] ?? ''}${p.last_name[0] ?? ''}`.toUpperCase();

    return (
      <div style={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1a3353 50%, #0f172a 100%)',
        borderRadius: 16,
        marginBottom: 28,
        overflow: 'hidden',
        boxShadow: '0 12px 48px rgba(15,23,42,0.4), 0 0 0 1px rgba(212,175,55,0.25)',
        position: 'relative' as const,
      }}>
        {/* Gold top accent */}
        <div style={{ height: 3, background: 'linear-gradient(90deg, transparent 0%, #d4af37 30%, #f5d060 50%, #d4af37 70%, transparent 100%)' }} />

        {/* Header */}
        <div style={{ padding: '28px 28px 20px', textAlign: 'center' as const }}>
          <div style={{ fontSize: 36, lineHeight: 1, marginBottom: 8 }}>🏆</div>
          <div style={{
            fontSize: 10, letterSpacing: '0.2em', fontWeight: 700, textTransform: 'uppercase' as const,
            color: '#d4af37', marginBottom: 8,
          }}>
            {isFinal ? 'Final Round' : 'Leaderboard Round'} — Official Pairings
          </div>
          <div style={{
            fontSize: 18, fontWeight: 800, color: '#f1f5f9', letterSpacing: '-0.01em', marginBottom: 6,
          }}>
            Final Groupings Announced
          </div>
          {(date || course) && (
            <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.5 }}>
              {date && <span>{fmtLongDate(date)}</span>}
              {date && course && <span style={{ margin: '0 6px', color: '#334155' }}>·</span>}
              {course && <span>{course}</span>}
            </div>
          )}
          <div style={{
            marginTop: 12, fontSize: 12, color: '#475569', fontStyle: 'italic',
          }}>
            Groupings based on standings after {gatedCount} completed round{gatedCount !== 1 ? 's' : ''}.
          </div>
        </div>

        {/* Divider */}
        <div style={{ margin: '0 28px', height: 1, background: 'rgba(212,175,55,0.15)' }} />

        {/* Group cards */}
        <div style={{
          padding: '20px 20px 28px',
          display: 'flex', gap: 14, flexWrap: 'wrap' as const, justifyContent: 'center' as const,
        }}>
          {lbGroups.map((g, gi) => (
            <div key={g.group_id} style={{
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(212,175,55,0.2)',
              borderRadius: 14,
              padding: '18px 20px',
              flex: '1 1 200px',
              maxWidth: 280,
              minWidth: 180,
              backdropFilter: 'blur(4px)',
            }}>
              {/* Group label + tee time */}
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                marginBottom: 14,
              }}>
                <div style={{
                  fontSize: 9, letterSpacing: '0.18em', fontWeight: 800,
                  textTransform: 'uppercase' as const, color: '#d4af37',
                }}>
                  Group {g.group_number}
                </div>
                {g.tee_time && (
                  <div style={{
                    fontSize: 10, fontWeight: 700, color: '#4ade80',
                    background: 'rgba(74,222,128,0.1)',
                    border: '1px solid rgba(74,222,128,0.2)',
                    borderRadius: 6, padding: '2px 7px',
                  }}>
                    {g.tee_time}
                  </div>
                )}
              </div>

              {/* Players */}
              <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 8 }}>
                {g.players.map((p, pi) => (
                  <div key={p.player_id} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    paddingBottom: pi < g.players.length - 1 ? 8 : 0,
                    borderBottom: pi < g.players.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                  }}>
                    <div style={{
                      width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                      background: gi % 2 === 0
                        ? 'linear-gradient(135deg, #d4af37, #f5d060)'
                        : 'linear-gradient(135deg, #60a5fa, #3b82f6)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 10, fontWeight: 800,
                      color: gi % 2 === 0 ? '#0f172a' : '#ffffff',
                      letterSpacing: '0.02em',
                    }}>
                      {initials(p)}
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#e2e8f0', lineHeight: 1.2 }}>
                        {p.first_name} {p.last_name}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Bottom accent */}
        <div style={{ height: 3, background: 'linear-gradient(90deg, transparent 0%, #d4af37 30%, #f5d060 50%, #d4af37 70%, transparent 100%)' }} />
      </div>
    );
  };

  // ── Legacy tournament leaderboard (no scheduler schedule) ──────────────────
  // For 2025-and-older tournaments that predate the per-player scheduler, build
  // the leaderboard straight from the recorded tournament rounds. Every round
  // is a column (date order), scores ungated.

  // Net / Gross / Points view switch — shared by both leaderboard tables. The
  // Points option only appears once the major has computed points.
  const renderViewToggle = () => {
    const modes: { key: 'net' | 'gross' | 'points'; label: string }[] = [
      { key: 'net', label: 'Net' },
      { key: 'gross', label: 'Gross' },
      ...(hasPoints ? [{ key: 'points' as const, label: 'Points' }] : []),
    ];
    return (
      <div style={{ marginLeft: 'auto', display: 'flex', background: '#f1f5f9', borderRadius: 8, padding: 3, gap: 2 }}>
        {modes.map(({ key, label }) => {
          const active = key === 'net' ? (!showGross && !showPoints) : key === 'gross' ? showGross : showPoints;
          return (
            <button key={key} onClick={() => setViewMode(key)} style={{
              padding: '4px 12px', fontSize: 12, fontWeight: 600, border: 'none',
              borderRadius: 6, cursor: 'pointer',
              background: active ? '#fff' : 'transparent',
              color: active ? '#1e293b' : '#94a3b8',
              boxShadow: active ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
            }}>
              {label}
            </button>
          );
        })}
      </div>
    );
  };

  const renderLegacyBreakdown = () => {
    const matches = tournamentRounds; // physical matches, already date ASC from the API
    if (matches.length === 0) {
      return (
        <div style={s.breakdownCard}>
          <div style={s.bdHeader}>
            <span style={{ fontSize: 18 }}>🏆</span>
            <h2 style={s.bdTitle}>Tournament Leaderboard</h2>
          </div>
          <div style={s.emptyState}>No matches recorded for this tournament yet.</div>
        </div>
      );
    }

    // Rows are built in utils/majorLeaderboard (shared with the Analysis view):
    // a player's matches folded into play order, counted rounds from the points
    // engine when it has them, and finishers → DQ → DNF → guests.
    const countedFromResults = Number(tournamentResults.find(r => Number(r.num_rounds) > 0)?.num_rounds ?? 0);
    const { rows: playerRows, ranked, countedRounds } =
      buildLegacyStandings(matches, countedFromResults, showGross ? 'gross' : 'net');
    const matchCount = matches.length;

    const leaderTotal = ranked[0]?.total ?? 0;
    const champions = ranked.filter(p => p.rank === 1);
    const showChampion = countedRounds > 0 && champions.length > 0 && !showPoints;
    const columnCount = Math.max(countedRounds, 1);
    const columnLabels = Array.from({ length: columnCount }, (_, i) => `R${i + 1}`);

    // Per-major points (placement + team bonus + total), keyed by full name.
    const resultByName = new Map<string, TournamentResult>();
    tournamentResults.forEach(r => resultByName.set(`${r.first_name} ${r.last_name}`, r));

    return (
      <>
      {showChampion && (
        <div style={{
          background: 'linear-gradient(135deg, #b45309 0%, #d4af37 50%, #b45309 100%)',
          color: '#fffbeb', borderRadius: 12, padding: '16px 22px', marginBottom: 16,
          display: 'flex', alignItems: 'center', gap: 14,
          boxShadow: '0 6px 20px rgba(180,83,9,0.25)',
        }}>
          <span style={{ fontSize: 32, lineHeight: 1 }}>🏆</span>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase' as const, color: 'rgba(255,251,235,0.85)' }}>
              {champions.length > 1 ? 'Co-Champions' : 'Champion'}
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.01em' }}>
              {champions.map(c => c.name).join(' & ')}
            </div>
          </div>
          <div style={{ marginLeft: 'auto', textAlign: 'right' as const }}>
            <div style={{ fontSize: 11, color: 'rgba(255,251,235,0.85)' }}>{showGross ? 'Gross' : 'Net'} total</div>
            <div style={{ fontSize: 22, fontWeight: 800 }}>{leaderTotal}</div>
          </div>
        </div>
      )}
      <div style={s.breakdownCard}>
        <div style={s.bdHeader}>
          <span style={{ fontSize: 18 }}>🏆</span>
          <h2 style={s.bdTitle}>Tournament Leaderboard</h2>
          <span style={s.badge}>{playerRows.length} players</span>
          <span style={s.badge} title="Matches entered vs rounds that count for competition — a session can be entered as several matches, by reservation">
            {matchCount} match{matchCount === 1 ? '' : 'es'} · {countedRounds} round{countedRounds === 1 ? '' : 's'}
          </span>
          {renderViewToggle()}
        </div>
        <FitTable>
          <table className="tbl tbl--sm">
            <thead>
              <tr>
                <th className="c col-fit">Rank</th>
                <th>Player</th>
                {showPoints ? (
                  <>
                    <th className="c" title="Placement points from the major's points distribution">Place</th>
                    <th className="c" title="Team bonus points">Bonus</th>
                    <th className="c" title="Total major points (placement + bonus)">Pts</th>
                  </>
                ) : (
                  <>
                    {columnLabels.map((label, i) => (
                      <th key={i} className="c" title="Round (a player's matches folded into play order)">{label}</th>
                    ))}
                    <th className="c">Total</th>
                    <th className="c">Back</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {playerRows.map((player) => {
                const dnf = !player.guest && !player.dq && !player.completed;
                const muted = player.dq || player.guest || dnf;
                const isLeader = !muted && player.rank === 1;
                return (
                  <tr key={player.name} style={isLeader ? { background: '#fffbeb' } : (player.guest ? { background: '#f8fafc' } : {})}>
                    <td className="c">
                      {player.guest
                        ? <span title="Guest — not in contention" style={{ color: '#cbd5e1', fontWeight: 700 }}>—</span>
                        : player.dq
                          ? <span title="Disqualified" style={{ color: '#dc2626', fontWeight: 800, fontSize: '0.85em' }}>DQ</span>
                          : dnf
                            ? <span title="Did not finish — didn't complete every round" style={{ color: '#b45309', fontWeight: 800, fontSize: '0.85em' }}>DNF</span>
                            : <span style={rankBadge(player.rank)}>{player.rank}</span>}
                    </td>
                    <td className="tbl-name" style={{ fontWeight: 600, color: muted ? '#94a3b8' : '#1e293b', textDecoration: player.dq ? 'line-through' : undefined }}>
                      <PlayerName full={player.name} />
                      {player.guest && (
                        <span className="tbl-tag" style={{ color: '#0369a1', background: '#e0f2fe' }}>Guest</span>
                      )}
                      {dnf && (
                        <span className="tbl-tag" style={{ color: '#b45309', background: '#fef3c7' }}>DNF</span>
                      )}
                    </td>
                    {showPoints ? (() => {
                      const r = resultByName.get(player.name);
                      return (
                        <>
                          <td className="c">{r ? fmtPts(r.placement_points) : '—'}</td>
                          <td className="c" style={{ color: r && Number(r.team_points) > 0 ? '#0369a1' : '#64748b', fontWeight: r && Number(r.team_points) > 0 ? 700 : 400 }}>
                            {r ? fmtPts(r.team_points) : '—'}
                          </td>
                          <td className="c" style={{ fontWeight: 800, color: '#0f172a' }}>{r ? fmtPts(r.total_points) : '—'}</td>
                        </>
                      );
                    })() : (
                      <>
                        {columnLabels.map((_, i) => (
                          <td key={i} className="c">{player.scores[i] ?? '—'}</td>
                        ))}
                        <td className="c" style={{ fontWeight: 700, color: muted ? '#94a3b8' : (isLeader ? '#92400e' : '#1e293b') }}>
                          {player.total}
                        </td>
                        <td className="c" style={{ color: '#64748b' }}>
                          {muted ? '—' : (player.total === leaderTotal ? '—' : `+${player.total - leaderTotal}`)}
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </FitTable>
      </div>
      </>
    );
  };

  // ── Tournament leaderboard table ────────────────────────────────────────────

  const renderTournamentBreakdown = () => {
    // Build leaderboard rows from the gated per-player schedule. Every scheduled
    // round counts, including the final round (it's each player's last column).
    const playerRows = buildScheduledStandings(scheduledEntries, showGross ? 'gross' : 'net');

    // Columns are numbered by round position and scale with the tournament's
    // configured round count: R1, R2, R3 for a 3-round major, R1–R4 for a
    // 4-round one, etc. The final round is just the last numbered round.
    const columnLabels = Array.from({ length: gatedCount }, (_, i) => `R${i + 1}`);

    const leaderTotal = playerRows[0]?.total ?? 0;
    const isGated = expectedRounds > gatedCount && expectedRounds > 0;

    // Per-major points (placement + team bonus + total), keyed by player_id.
    const resultById = new Map<number, TournamentResult>();
    tournamentResults.forEach(r => resultById.set(r.player_id, r));

    // The tournament is decided once every round (including the final) is released
    // for all players. Declaring the champion is the headline of this view.
    const tournamentComplete = expectedRounds > 0 && gatedCount >= expectedRounds;

    // The champion is ALWAYS decided on NET — the Gross/Net toggle only changes
    // the table view, never who is crowned.
    const netRows = buildScheduledStandings(scheduledEntries, 'net');
    const champions = netRows.filter(p => p.rank === 1);
    const championTotal = netRows[0]?.total ?? 0;

    return (
      <>
        {tournamentComplete && champions.length > 0 && (
          <div style={{
            background: 'linear-gradient(135deg, #b45309 0%, #d4af37 50%, #b45309 100%)',
            color: '#fffbeb', borderRadius: 12, padding: '16px 22px', marginBottom: 16,
            display: 'flex', alignItems: 'center', gap: 14,
            boxShadow: '0 6px 20px rgba(180,83,9,0.25)',
          }}>
            <span style={{ fontSize: 32, lineHeight: 1 }}>🏆</span>
            <div>
              <div style={{
                fontSize: 10, fontWeight: 700, letterSpacing: '0.18em',
                textTransform: 'uppercase' as const, color: 'rgba(255,251,235,0.85)',
              }}>
                {champions.length > 1 ? 'Co-Champions' : 'Champion'}
              </div>
              <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.01em' }}>
                {champions.map(c => c.name).join(' & ')}
              </div>
            </div>
            <div style={{ marginLeft: 'auto', textAlign: 'right' as const }}>
              <div style={{ fontSize: 11, color: 'rgba(255,251,235,0.85)' }}>
                Net total
              </div>
              <div style={{ fontSize: 22, fontWeight: 800 }}>{championTotal}</div>
            </div>
          </div>
        )}
      <div style={s.breakdownCard}>
        <div style={s.bdHeader}>
          <span style={{ fontSize: 18 }}>🏆</span>
          <h2 style={s.bdTitle}>Tournament Leaderboard</h2>
          <span style={s.badge}>{playerRows.length} players</span>
          <span style={s.badge} title="Matches entered vs rounds that count for competition — a session can be entered as several matches, by reservation">
            {tournamentRounds.length} match{tournamentRounds.length === 1 ? '' : 'es'} · {expectedRounds} round{expectedRounds === 1 ? '' : 's'}
          </span>
          {isGated && (
            <span style={{
              fontSize: 11, fontWeight: 600, color: '#92400e',
              background: '#fef3c7', border: '1px solid #fde68a',
              borderRadius: 6, padding: '2px 8px',
            }}>
              {gatedCount} of {expectedRounds} rounds released
            </span>
          )}
          {renderViewToggle()}
        </div>
        {gatedCount === 0 ? (
          <div style={s.emptyState}>
            No rounds have been completed by all players yet.
          </div>
        ) : (
          <FitTable>
            <table className="tbl tbl--sm">
              <thead>
                <tr>
                  <th className="c col-fit">Rank</th>
                  <th>Player</th>
                  {showPoints ? (
                    <>
                      <th className="c" title="Placement points from the major's points distribution">Place</th>
                      <th className="c" title="Team bonus points">Bonus</th>
                      <th className="c" title="Total major points (placement + bonus)">Pts</th>
                    </>
                  ) : (
                    <>
                      {columnLabels.map((label, i) => (
                        <th key={i} className="c">{label}</th>
                      ))}
                      <th className="c">Total</th>
                      <th className="c">Back</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {playerRows.map((player) => (
                  <tr key={player.player_id} style={player.rank === 1 ? { background: '#fffbeb' } : {}}>
                    <td className="c">
                      <span style={rankBadge(player.rank)}>{player.rank}</span>
                    </td>
                    <td className="tbl-name" style={{ fontWeight: 600, color: '#1e293b' }}><PlayerName full={player.name} /></td>
                    {showPoints ? (() => {
                      const r = resultById.get(player.player_id);
                      return (
                        <>
                          <td className="c">{r ? fmtPts(r.placement_points) : '—'}</td>
                          <td className="c" style={{ color: r && Number(r.team_points) > 0 ? '#0369a1' : '#64748b', fontWeight: r && Number(r.team_points) > 0 ? 700 : 400 }}>
                            {r ? fmtPts(r.team_points) : '—'}
                          </td>
                          <td className="c" style={{ fontWeight: 800, color: '#0f172a' }}>{r ? fmtPts(r.total_points) : '—'}</td>
                        </>
                      );
                    })() : (
                      <>
                        {player.scores.map((sc, i) => (
                          <td key={i} className="c">{sc ?? '—'}</td>
                        ))}
                        <td className="c" style={{ fontWeight: 700, color: player.rank === 1 ? '#92400e' : '#1e293b' }}>
                          {player.total}
                        </td>
                        <td className="c" style={{ color: '#64748b' }}>
                          {player.total === leaderTotal ? '—' : `+${(player.total as number) - leaderTotal}`}
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </FitTable>
        )}
      </div>
      </>
    );
  };

  // Locked-score appearance: blurred + unselectable until released.
  const blurStyle: React.CSSProperties = {
    filter: 'blur(6px)',
    userSelect: 'none',
    pointerEvents: 'none',
    color: '#94a3b8',
  };
  // Render a blurred MASK instead of the real value while a score is locked, so
  // the actual number is never written into the DOM — it can't be recovered by
  // inspecting the HTML; only the placeholder dots are present.
  const maskedScore = (value: React.ReactNode, released: boolean) =>
    released ? value : (
      <span style={blurStyle} title="Locked until all players complete this round" aria-hidden>
        •••
      </span>
    );

  const renderRoundCard = (round: Round, isTournament: boolean, gateScores = false) => {
    const hasPoints = !isTournament && round.players.some(p => p.points != null && p.points > 0);
    // The PEN column only appears on a card where someone was actually
    // assessed penalty strokes; the badge's tooltip carries the reason.
    const penaltyOf = (p: RoundPlayer) => (p.is_disqualified ? 0 : Number(p.penalty_strokes) || 0);
    const hasPenalties = round.players.some(p => penaltyOf(p) > 0);
    const meta = roundMeta[round.round_id];
    const sessionLabel = meta?.is_leaderboard_round
      ? (meta.is_final_round ? 'Final Round' : 'Leaderboard Round')
      : meta?.round_number != null ? `Tournament Round ${meta.round_number}` : null;

    // Per-player release state, and whether this round still has any locked score.
    const decorated = round.players.map(p => ({
      player: p,
      released: !gateScores || isScoreReleased(round.round_id, p.first_name, p.last_name),
    }));
    const roundHasBlur = decorated.some(d => !d.released);

    // A "guest" is a player who isn't competing in the tournament (e.g. a league
    // member tracking their handicap, like Elias). It's either stored on the
    // score (legacy majors) or, for scheduler tournaments, derived from the
    // invite roster. Guests are pushed to the bottom and excluded from ranking.
    const isGuest = (p: RoundPlayer) =>
      !!p.is_guest || (isTournament && pps.length > 0 && !nameToPlayerId.has(`${p.first_name} ${p.last_name}`));
    const hasGuests = decorated.some(d => isGuest(d.player));

    // Competition ranks over the tournament players only (net ascending, ties
    // skip). Guests get no rank. Without guests, keep the backend's ranks as-is.
    const rankByName = new Map<string, number>();
    if (hasGuests) {
      const ranked = decorated
        .filter(d => !isGuest(d.player))
        .map(d => d.player)
        .sort((a, b) => (a.net_score ?? Number.POSITIVE_INFINITY) - (b.net_score ?? Number.POSITIVE_INFINITY));
      let rk = 0, prev: number | null = null;
      ranked.forEach((p, i) => {
        const net = p.net_score ?? Number.POSITIVE_INFINITY;
        if (prev === null || net !== prev) { rk = i + 1; prev = net; }
        rankByName.set(`${p.first_name} ${p.last_name}`, rk);
      });
    }

    // When a round still has locked scores, float the released players to the top
    // ordered by who did better (lowest net first) and hold the locked players
    // below in a stable name order that doesn't betray their standing. The round's
    // real ranks stay hidden until nothing is blurred. With everything released we
    // keep the backend's net-ascending order. Either way, guests sort to the end.
    const blurSort = (list: typeof decorated) => roundHasBlur
      ? [...list].sort((a, b) => {
          if (a.released !== b.released) return a.released ? -1 : 1;
          if (a.released) {
            return (a.player.net_score ?? Number.POSITIVE_INFINITY)
              - (b.player.net_score ?? Number.POSITIVE_INFINITY);
          }
          return `${a.player.last_name} ${a.player.first_name}`
            .localeCompare(`${b.player.last_name} ${b.player.first_name}`);
        })
      : list;
    const orderedPlayers = [
      ...blurSort(decorated.filter(d => !isGuest(d.player))),
      ...blurSort(decorated.filter(d => isGuest(d.player))),
    ];

    return (
      <div key={round.round_id} style={s.roundCard}>
        <div style={s.roundCardHeader}>
          <div style={s.roundInfo}>
            <div style={s.roundDate}>{round.date}</div>
            <div style={s.roundCourseLine}>
              <span style={s.roundCourseName}>{round.course_name}</span>
              {round.facility_name && round.facility_name !== round.course_name && (
                <span style={s.roundFacility}>{round.facility_name}</span>
              )}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
              <span style={teeboxBadge(round.course_teebox_name)}>{round.course_teebox_name}</span>
              {sessionLabel && (
                <span style={{
                  fontSize: 11, fontWeight: 700, color: '#3730a3',
                  background: '#e0e7ff', borderRadius: 5, padding: '2px 9px',
                }}>
                  {sessionLabel}
                </span>
              )}
              {meta?.am_pm && (
                <span style={{
                  fontSize: 11, fontWeight: 600, color: '#0369a1',
                  background: '#e0f2fe', borderRadius: 5, padding: '2px 9px',
                }}>
                  {meta.am_pm === 'AM' ? '☀ Morning' : '🌆 Afternoon'}
                </span>
              )}
              {meta?.tee_time && (
                <span style={{
                  fontSize: 11, fontWeight: 600, color: '#047857',
                  background: '#d1fae5', borderRadius: 5, padding: '2px 9px',
                }}>
                  ⏰ {(meta.tee_times && meta.tee_times.length > 0 ? meta.tee_times : [meta.tee_time]).join(' · ')}
                </span>
              )}
              {meta?.session_match_count != null && meta.session_match_count > 1 && (
                <span
                  title={`This session's scores were entered as ${meta.session_match_count} matches (by reservation); each counts toward the same round.`}
                  style={{
                    fontSize: 11, fontWeight: 600, color: '#92400e',
                    background: '#fef3c7', borderRadius: 5, padding: '2px 9px',
                  }}
                >
                  Split session · {meta.session_match_index} of {meta.session_match_count}
                </span>
              )}
              {renderScorecardChip(round.round_id)}
            </div>
            {meta && (meta.reservation_name || meta.complex_name || meta.reservation_course_name) && (
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                📋 {[
                  meta.reservation_name,
                  meta.complex_name,
                  meta.reservation_course_name && meta.reservation_course_name !== round.course_name
                    ? meta.reservation_course_name : null,
                ].filter(Boolean).join(' · ')}
              </div>
            )}
          </div>
          <div style={s.roundActions}>
            <ScorecardUploadButton
              roundId={round.round_id}
              getToken={requireAccessToken(getAccessTokenSilently)}
              style={s.btnInfo}
              className=""
            />
            <button style={s.btnInfo} onClick={() => handleViewByHoleModalShow(round.round_id)}>
              By Hole
            </button>
            <button style={s.btnDanger} onClick={() => deleteRound(round.round_id)}>
              Delete
            </button>
          </div>
        </div>
        {round.players.length > 0 ? (
          <FitTable>
            <table className="tbl tbl--sm">
              <thead>
                <tr>
                  <th className="c col-fit">#</th>
                  <th>Player</th>
                  <th className="c">Gross</th>
                  <th className="c">HCP</th>
                  {hasPenalties && (
                    <th className="c" title="Penalty strokes — added to the net score after the handicap. Click a badge for the reason.">PEN</th>
                  )}
                  <th className="c">Net</th>
                  {hasPoints && <th className="c">Pts</th>}
                </tr>
              </thead>
              <tbody>
                {orderedPlayers.map(({ player: p, released }, idx) => {
                  // Hide every player's real rank while any score in the round is
                  // still locked — a blurred score could land anywhere, so no
                  // standing is final yet. Ranks reappear once nothing is blurred.
                  const guest = isGuest(p);
                  const dq = !!p.is_disqualified;
                  const myRank = guest ? 0 : (hasGuests ? (rankByName.get(`${p.first_name} ${p.last_name}`) ?? 0) : p.rank);
                  const isLeader = !roundHasBlur && !guest && !dq && myRank === 1;
                  const chSign = p.course_handicap > 0 ? '+' : '';
                  const chColor = p.course_handicap < 0 ? '#16a34a' : p.course_handicap > 0 ? '#dc2626' : '#64748b';
                  return (
                    <tr key={`${p.first_name} ${p.last_name} ${idx}`} style={isLeader ? { background: '#fffbeb' } : (guest ? { background: '#fafafa' } : {})}>
                      <td className="c">
                        {dq
                          ? <span title={p.dq_reason || 'Disqualified'} style={{ color: '#dc2626', fontWeight: 800, fontSize: '0.85em' }}>DQ</span>
                          : roundHasBlur
                            ? <span style={rankBadge(0)} title="Ranking hidden until all scores are in">–</span>
                            : guest
                              ? <span title="Not part of the tournament — excluded from the ranking" style={{ color: '#cbd5e1', fontWeight: 700 }}>—</span>
                              : <span style={rankBadge(myRank)}>{myRank}</span>}
                      </td>
                      <td className="tbl-name" style={{ fontWeight: isLeader ? 600 : 400, color: dq ? '#94a3b8' : (guest ? '#94a3b8' : (isLeader ? '#1e293b' : '#374151')), textDecoration: dq ? 'line-through' : undefined }}>
                        <PlayerName first={p.first_name} last={p.last_name} />
                        {guest && (
                          <span className="tbl-tag" style={{ color: '#94a3b8', background: '#f1f5f9' }}>Guest</span>
                        )}
                        {dq && (
                          <span title={p.dq_reason || 'Disqualified'} className="tbl-tag" style={{ color: '#dc2626', background: '#fef2f2', textDecoration: 'none', cursor: 'help' }}>DQ</span>
                        )}
                      </td>
                      <td className="c">{dq ? '—' : maskedScore(p.gross_score, released)}</td>
                      <td className="c" style={{ color: released ? chColor : undefined, fontWeight: 500 }}>
                        {dq ? '—' : maskedScore(`${chSign}${p.course_handicap}`, released)}
                      </td>
                      {hasPenalties && (
                        <td className="c">
                          {dq ? '—' : maskedScore(
                            penaltyOf(p) > 0
                              ? <PenaltyBadge
                                  strokes={penaltyOf(p)}
                                  reason={p.penalty_reason}
                                  playerName={`${p.first_name} ${p.last_name}`}
                                />
                              : <span style={{ color: '#cbd5e1' }}>—</span>,
                            released,
                          )}
                        </td>
                      )}
                      <td className="c" style={{ fontWeight: 700, color: '#1e293b' }}>{dq ? '—' : maskedScore(p.net_score, released)}</td>
                      {hasPoints && (
                        <td className="c" style={{ color: '#2563eb', fontWeight: 500 }}>
                          {dq ? '—' : maskedScore(p.points && p.points > 0 ? p.points.toFixed(2) : '—', released)}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </FitTable>
        ) : (
          <div style={{ padding: '14px 18px', color: '#94a3b8', fontSize: 13 }}>No scores recorded</div>
        )}
      </div>
    );
  };

  const renderRoundsSection = (rounds: Round[], title: string, icon: string, isTournament: boolean, gateScores = false, groupByDay = false) => {
    // Group rounds under day headers (tournament view). Uses the schedule's
    // slot_date when available, falling back to the round's own date. Within a
    // day, matches run in play order — morning, then afternoon, by tee time —
    // not entry order: a session split across courses is entered one course at
    // a time, possibly after a later session.
    const dayKey = (r: Round) => roundMeta[r.round_id]?.slot_date || r.date;
    const playOrder = (r: Round) => ({ ...roundMeta[r.round_id], round_id: r.round_id });
    const dayGroups: { day: string; rounds: Round[] }[] = [];
    if (groupByDay) {
      const map = new Map<string, Round[]>();
      const order: string[] = [];
      for (const r of rounds) {
        const k = dayKey(r);
        if (!map.has(k)) { map.set(k, []); order.push(k); }
        map.get(k)!.push(r);
      }
      order.sort();
      order.forEach(day => dayGroups.push({
        day,
        rounds: map.get(day)!.sort((a, b) => compareMatchesInDay(playOrder(a), playOrder(b))),
      }));
    }
    // Tournament cards are matches: a round of the major can be several of them.
    const count = (n: number) => (isTournament
      ? `${n} match${n !== 1 ? 'es' : ''}`
      : `${n} round${n !== 1 ? 's' : ''}`);

    return (
      <div style={s.sectionCard}>
        <div style={s.sectionHeader}>
          <span style={{ fontSize: 18 }}>{icon}</span>
          <h2 style={s.sectionTitle}>{title}</h2>
          <span style={s.badge}>{count(rounds.length)}</span>
          {gateScores && (
            <span style={{ marginLeft: 'auto', fontSize: 11, color: '#64748b', display: 'flex', alignItems: 'center', gap: 5 }}>
              🔒 Locked scores unlock once all players finish that round
            </span>
          )}
        </div>
        {rounds.length === 0 ? (
          <div style={s.emptyState}>No rounds recorded for this season.</div>
        ) : groupByDay ? (
          <div style={{ padding: '12px 20px 20px', display: 'flex', flexDirection: 'column', gap: 26 }}>
            {dayGroups.map(({ day, rounds: dayRounds }) => (
              <div key={day}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12,
                  paddingBottom: 8, borderBottom: '2px solid #e2e8f0',
                }}>
                  <span style={{ fontSize: 15 }}>📅</span>
                  <span style={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>{fmtLongDate(day)}</span>
                  <span style={s.badge}>{count(dayRounds.length)}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
                  {dayRounds.map(round => renderRoundCard(round, isTournament, gateScores))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={s.roundsList}>
            {rounds.map(round => renderRoundCard(round, isTournament, gateScores))}
          </div>
        )}
      </div>
    );
  };

  // ── Analysis view inputs ─────────────────────────────────────────────────
  // Default order everywhere in the Analysis = the leaderboard's NET finishing
  // order, built by the same code as the leaderboard (utils/majorLeaderboard).
  const analysisCounted = Number(tournamentResults.find(r => Number(r.num_rounds) > 0)?.num_rounds ?? 0);
  const standingsNow = !selectedTournamentId ? [] : pps.length > 0
    ? standingsFromScheduled(buildScheduledStandings(scheduledEntries, 'net'))
    : standingsFromLegacy(buildLegacyStandings(tournamentRounds, analysisCounted, 'net').rows);
  // Scheduled majors: each player's released rounds, in the leaderboard's order.
  const roundOrderNow: [number, number[]][] | null = pps.length > 0
    ? pps.map(pp => [pp.player_id, (playerCounting.get(pp.player_id) ?? []).slice(0, gatedCount)
      .map(e => e.round_id).filter((id): id is number => id != null)])
    : null;
  // Stable identities (the page re-renders often) so the Analysis only
  // recomputes when the standings or released rounds actually change.
  const standingsKey = JSON.stringify(standingsNow);
  const roundOrderKey = JSON.stringify(roundOrderNow);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const analysisStandings = useMemo(() => standingsNow, [standingsKey]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const analysisRoundOrder = useMemo(() => (roundOrderNow ? new Map(roundOrderNow) : null), [roundOrderKey]);
  const analysisToken = useMemo(() => requireAccessToken(getAccessTokenSilently), [getAccessTokenSilently]);
  const analysisReady = selectedTournamentId != null
    && roundsLoadedFor === selectedTournamentId && contextLoadedFor === selectedTournamentId;
  const releasedNote = pps.length > 0 && gatedCount > 0 && gatedCount < expectedRounds
    ? `${gatedCount} of ${expectedRounds} rounds released to everyone — the analysis covers the released rounds only.`
    : null;

  // For tournament view: filter round cards to only released ones when schedule is available
  const visibleTournamentRounds = selectedTournamentId && pps.length > 0
    ? tournamentRounds.filter(r => visibleRoundIds.has(r.round_id))
    : tournamentRounds;

  return (
    <div style={s.page}>
      <div style={s.inner}>
      <div style={s.topBar}>
        <SeasonFilter selectedSeason={selectedSeason} setSelectedSeason={handleSeasonChange} />
        {tournaments.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={s.filterLabel}>Tournament</span>
            <select
              style={s.select}
              value={selectedTournamentId ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                setSelectedTournamentId(v ? parseInt(v) : null);
              }}
            >
              <option value="">None</option>
              {tournaments.map((t) => (
                <option key={t.tournament_id} value={t.tournament_id}>{t.tournament_name}</option>
              ))}
            </select>
          </div>
        )}
        <button style={s.addBtn} onClick={handleModalShow}>+ Add Round</button>
      </div>

      <Modal
        show={modalShow}
        onHide={handleModalClose}
        title="Adding a new round"
        onRoundAdded={() => { fetchRegularRounds(); fetchTournamentRounds(); fetchScheduleContext(); setAnalysisReload(n => n + 1); }}
      />

      {selectedTournamentId && (
        <div className="ma-view-switch" role="tablist" aria-label="Major view">
          <button type="button" role="tab" aria-selected={majorView === 'leaderboard'}
            className={majorView === 'leaderboard' ? 'active' : ''} onClick={() => setMajorView('leaderboard')}>
            🏆 Leaderboard
          </button>
          <button type="button" role="tab" aria-selected={majorView === 'analysis'}
            className={majorView === 'analysis' ? 'active' : ''} onClick={() => setMajorView('analysis')}>
            📊 Analysis
          </button>
        </div>
      )}

      {selectedTournamentId && majorView === 'analysis' && (
        analysisReady ? (
          <MajorAnalysis
            key={selectedTournamentId}
            tournamentId={selectedTournamentId}
            standings={analysisStandings}
            roundOrder={analysisRoundOrder}
            getAccessTokenSilently={analysisToken}
            onOpenRound={handleViewByHoleModalShow}
            reloadKey={analysisReload}
            releasedNote={releasedNote}
          />
        ) : (
          <div style={s.emptyState}>Loading the analysis…</div>
        )
      )}

      {selectedTournamentId && majorView === 'leaderboard' && (
        pps.length > 0 ? (
          <>
            {allPreLbDone && gatedCount < expectedRounds && lbGroups.length > 0 && renderFinalGroupingsAnnouncement(false)}
            {renderTournamentBreakdown()}
            {gatedCount >= expectedRounds && lbGroups.length > 0 && renderFinalGroupingsAnnouncement(true)}
          </>
        ) : (
          /* Legacy path: tournaments without a scheduler schedule (2025 & older) */
          renderLegacyBreakdown()
        )
      )}

      {!selectedTournamentId && renderRoundsSection(regularRounds, 'Regular Season Rounds', '⛳', false)}

      {/* Major rounds only appear inside the tournament view.
          With a schedule, gate to released rounds; without one, show all. */}
      {selectedTournamentId && majorView === 'leaderboard' && (pps.length === 0 || gatedCount > 0) && renderRoundsSection(
        visibleTournamentRounds,
        'Major Matches',
        '🏅',
        true,
        pps.length > 0,  // gate per-player scores only when a schedule exists
        true,            // group major rounds by day
      )}

      <ViewRoundByHole
        isOpen={viewByHoleModalOpen}
        onClose={() => { handleViewByHoleModalClose(); fetchCardSummaries(); setAnalysisReload(n => n + 1); }}
        roundId={selectedRoundId}
        getAccessTokenSilently={requireAccessToken(getAccessTokenSilently)}
        onChanged={fetchCardSummaries}
      />
      </div>
    </div>
  );
};

export default Rounds;
